<template>
    <div class="payment-methods-root">
        <div
            class="payment-container"
            :class="{ 'variant-divider': variant === 'divider' }"
        >
            <span v-if="showTitle" class="payment-title">{{
                authorizationRequired
                    ? $t("m.selectAuthorizationMethod")
                    : $t("m.selectPayMethod")
            }}</span>

            <div v-for="(item, index) in cardList" :key="index">
                <div
                    class="payment-method"
                    :class="{
                        'highlight-flash': isHighlighted(item.id),
                        active: isSavedCardSelected(item),
                    }"
                    v-active-touch
                    @click="handleSavedCardClick(item)"
                >
                    <div class="left card">
                        <img
                            :src="getBrandIcon(item.brand)"
                            alt="credit card icon"
                        />
                    </div>
                    <span class="center"
                        >•••• •••• ••••
                        {{ item?.last4 !== "" ? item.last4 : "••••" }}</span
                    >
                    <div class="right-container">
                        <div
                            v-if="isShowDelete"
                            v-active-touch
                            class="delete-btn interactive-text"
                            @click.stop="handleDeleteCard(item)"
                        >
                            Delete
                        </div>
                        <span
                            v-else
                            class="right"
                            :class="{
                                active: isSavedCardSelected(item),
                            }"
                        ></span>
                    </div>
                </div>
            </div>

            <div
                v-if="showBalance"
                class="payment-method balance-method"
                :class="{
                    'highlight-flash': isPaymentMethodHighlighted('balance'),
                    active: selectedMethod === 'balance',
                }"
                v-active-touch
                @click="handleBalanceClick"
            >
                <div class="left">
                    <BalanceIcon />
                </div>
                <span class="center balance-center">
                    <div class="top">
                        <span>{{ $t("m.balance") }}</span>
                        <span class="current-balance"
                            >({{ formattedAccount(userBalance) }})</span
                        >
                    </div>
                </span>
                <span
                    v-if="rechargeMode"
                    v-active-touch
                    class="right recharge interactive-text"
                    @click.stop="$emit('show-recharge')"
                >
                    {{ $t("m.recharge") }}
                </span>
                <span
                    v-else
                    class="right"
                    :class="{ active: selectedMethod === 'balance' }"
                ></span>
            </div>

            <div
                v-for="method in filteredPaymentMethods"
                :key="`${method.gateway}-${method.method}`"
            >
                <MoyasarInlineApplePay
                    v-if="isInlineMoyasarApplePay(method)"
                    :selected-subscription="selectedSubscription"
                    :authorization-subscription-id="authorizationSubscriptionId"
                    :device-code="deviceCode"
                    :outlet-id="outletId"
                    :payment-config="paymentConfig"
                    :create-payment-order="createPaymentOrder"
                    :do-subscription-payment="doSubscriptionPayment"
                    :toast="emitToast"
                />
                <div
                    v-else
                    class="payment-method"
                    :class="{
                        pending: method.isAvailabilityChecking,
                        active:
                            selectedMethod === method.method &&
                            method.method !== 'card' &&
                            method.method !== 'web_card',
                    }"
                    v-active-touch
                    @touchstart.passive="handleMethodPreload(method)"
                    @mousedown.passive="handleMethodPreload(method)"
                    @click="handleMethodClick(method)"
                >
                    <div
                        class="left"
                        :class="{
                            card:
                                method.method === 'card' ||
                                method.method === 'web_card' ||
                                method.method === 'creditcard',
                        }"
                    >
                        <AppleIcon
                            v-if="
                                method.method === 'apple_pay' ||
                                method.method === 'web_apple_pay' ||
                                method.method === 'applepay'
                            "
                        />
                        <GoogleIcon
                            v-if="
                                method.method === 'google_pay' ||
                                method.method === 'web_google_pay'
                            "
                        />
                        <CreditCardIcon
                            v-if="
                                method.method === 'card' ||
                                method.method === 'web_card' ||
                                method.method === 'creditcard'
                            "
                        />
                        <img
                            v-if="method.method === 'oneclick'"
                            src="@/assets/payments/oneclick.webp"
                            alt="oneclick icon"
                        />
                        <img
                            v-if="method.method === 'thawani'"
                            src="@/assets/payments/oneclick.webp"
                            alt="thawani icon"
                        />
                    </div>
                    <span class="center">
                        {{ getMethodLabel(method.method) }}
                    </span>
                    <span
                        v-if="method.isAvailabilityChecking"
                        class="right wallet-loading"
                        aria-hidden="true"
                    ></span>
                    <span
                        v-else-if="
                            enableSpecialAddAction && method.method === 'oneclick'
                        "
                        v-active-touch
                        class="right add-card interactive-text"
                        @click.stop="handleOneclickAdd(method)"
                    >
                        {{ $t(specialAddLabelKey) }}
                    </span>
                    <span
                        v-else-if="
                            enableSpecialAddAction && method.method === 'thawani'
                        "
                        v-active-touch
                        class="right add-card interactive-text"
                        @click.stop="handleThawaniPay(method)"
                    >
                        {{ $t(specialAddLabelKey) }}
                    </span>
                    <span
                        v-else
                        class="right"
                        :class="{
                            active:
                                selectedMethod === method.method &&
                                method.method !== 'card' &&
                                method.method !== 'web_card',
                            'bind-card':
                                method.method === 'card' ||
                                method.method === 'web_card',
                        }"
                    >
                        <PlusIcon
                            v-if="
                                method.method === 'card' ||
                                method.method === 'web_card'
                            "
                        />
                    </span>
                </div>
            </div>
        </div>
        <BindCardPopup
            v-if="enableBindCardPopup"
            ref="bindCardPopup"
            :showPopup="isShowBindPopup"
            :isShowBack="isShowBack"
            :cardGatewayId="cardGatewayId"
            :stripeGatewayId="stripeGatewayId"
            :fortisGatewayId="fortisGatewayId"
            :stripe="stripe"
            :selectedSubscription="selectedSubscription"
            :authorizationSubscriptionId="authorizationSubscriptionId"
            :deviceCode="deviceCode"
            :outletId="outletId"
            @hidePopup="handleHideBindPopup"
            @show-loading="$emit('show-loading')"
            @hide-loading="$emit('hide-loading')"
            @station-busy-change="$emit('station-busy-change', $event)"
            @toast="$emit('toast', $event)"
            @guest-required="$emit('guest-required')"
            @subscription-success="$emit('subscription-success')"
        />
    </div>
</template>

<script>
import AppleIcon from "../../../components/icons/AppleIcon.vue";
import GoogleIcon from "../../../components/icons/GoogleIcon.vue";
import CreditCardIcon from "../../../components/icons/CreditCardIcon.vue";
import PlusIcon from "../../../components/icons/PlusIcon.vue";
import BalanceIcon from "../../../components/icons/BalanceIcon.vue";
import BindCardPopup from "./BindCardPopup.vue";
import MoyasarInlineApplePay from "./MoyasarInlineApplePay.vue";
import { brandIconList } from "@/common/payment";
import { deleteCard } from "@/api/logined";

export default {
    components: {
        AppleIcon,
        GoogleIcon,
        CreditCardIcon,
        PlusIcon,
        BalanceIcon,
        BindCardPopup,
        MoyasarInlineApplePay,
    },
    props: {
        showTitle: {
            type: Boolean,
            default: true,
        },
        variant: {
            type: String,
            default: "card",
        },
        enableSpecialAddAction: {
            type: Boolean,
            default: true,
        },
        specialAddLabelKey: {
            type: String,
            default: "m.saveRent",
        },
        showBalance: {
            type: Boolean,
            default: false,
        },
        userBalance: {
            type: Number,
            default: 0,
        },
        rechargeMode: {
            type: Boolean,
            default: true,
        },
        paymentMethods: {
            type: Array,
            required: true,
        },
        cardList: {
            type: Array,
            default: () => [],
        },
        selectedMethod: {
            type: String,
            default: undefined,
        },
        selectedCardId: {
            type: [String, Number],
            default: undefined,
        },
        cardGatewayId: {
            type: Number,
            default: undefined,
        },
        stripeGatewayId: {
            type: Number,
            default: undefined,
        },
        fortisGatewayId: {
            type: Number,
            default: undefined,
        },
        stripe: {
            type: Object,
            default: undefined,
        },
        selectedSubscription: {
            type: Object,
            default: undefined,
        },
        authorizationSubscriptionId: {
            type: [String, Number],
            default: undefined,
        },
        deviceCode: {
            type: [String, Number],
            required: true,
        },
        outletId: {
            type: [String, Number],
            default: undefined,
        },
        enableBindCardPopup: {
            type: Boolean,
            default: true,
        },
        authorizationRequired: {
            type: Boolean,
            default: false,
        },
        beforeBindCard: {
            type: Function,
            default: undefined,
        },
        paymentConfig: {
            type: Object,
            default: () => ({}),
        },
        createPaymentOrder: {
            type: Function,
            default: undefined,
        },
        doSubscriptionPayment: {
            type: Function,
            default: undefined,
        },
    },
    data() {
        return {
            isShowBindPopup: false,
            isShowBack: false,
            brandIconList: brandIconList,
            isShowDelete: false,
        };
    },
    computed: {
        filteredPaymentMethods() {
            const methods = this.paymentMethods.filter(
                (method) => method.isAvailable || method.isAvailabilityChecking
            );

            return methods.sort((a, b) => {
                const aIsMoyasarApplePay =
                    a?.gateway === "moyasar" && a?.method === "applepay";
                const bIsMoyasarApplePay =
                    b?.gateway === "moyasar" && b?.method === "applepay";

                if (aIsMoyasarApplePay === bIsMoyasarApplePay) return 0;
                return aIsMoyasarApplePay ? 1 : -1;
            });
        },
    },
    methods: {
        handleBalanceClick() {
            if (this.rechargeMode) {
                this.$emit("show-recharge");
            } else {
                this.$emit("update:selectedMethod", "balance");
            }
        },
        isHighlighted(cardId) {
            const highlightId = this.$store.getters.highlight_card_id;
            return highlightId && Number(cardId) === Number(highlightId);
        },
        isPaymentMethodHighlighted(method) {
            const highlightMethod = this.$store.getters.highlight_payment_method;
            return highlightMethod && String(highlightMethod) === String(method);
        },
        isInlineMoyasarApplePay(method) {
            return (
                method?.gateway === "moyasar" &&
                method?.method === "applepay" &&
                !!this.paymentConfig?.moyasarGatewayId
            );
        },
        getMethodLabel(method) {
            switch (method) {
                case "apple_pay":
                case "web_apple_pay":
                case "applepay":
                    return this.$t("m.applePay");
                case "google_pay":
                case "web_google_pay":
                    return this.$t("m.googlePay");
                case "card":
                case "web_card":
                case "creditcard":
                case "oneclick":
                case "thawani":
                    return this.$t("m.creditOrDebit");
                default:
                    return method;
            }
        },
        getBrandIcon(brand) {
            const icon = this.brandIconList.find(
                (item) => item.brand === brand
            );
            return icon
                ? icon.icon
                : require("@/assets/payments/credit_card.svg");
        },
        handleMethodClick(method) {
            if (method.isAvailabilityChecking || !method.isAvailable) {
                return;
            }
            if (this.enableSpecialAddAction) {
                if (method.method === "oneclick") {
                    this.handleOneclickAdd(method);
                    return;
                }
                if (method.method === "thawani") {
                    this.handleThawaniPay(method);
                    return;
                }
            }

            if (method.method === "card" || method.method === "web_card") {
                if (this.beforeBindCard && this.beforeBindCard() === false) {
                    return;
                }
                this.handleShowBind(method);
            } else {
                this.$emit("update:selectedMethod", method.method);
            }
        },
        handleSavedCardClick(item) {
            this.$emit("update:selectedMethod", "card");
            this.$emit("update:selectedCardId", item.id);
        },
        isSavedCardSelected(item) {
            return (
                this.selectedMethod === "card" &&
                String(this.selectedCardId) === String(item.id)
            );
        },
        handleOneclickAdd(method) {
            this.$emit("oneclick-add", method);
        },
        handleThawaniPay(method) {
            this.$emit("thawani-pay", method);
        },
        emitToast(message) {
            this.$emit("toast", message);
        },
        handleMethodPreload(method) {
            if (!method || (method.method !== "card" && method.method !== "web_card")) {
                return;
            }
            if (this.enableBindCardPopup) {
                this.$refs.bindCardPopup?.preloadBindCard({
                    prepareIntent:
                        Number(this.cardGatewayId) === Number(this.fortisGatewayId),
                });
                return;
            }
            this.$emit("prefetch-bind-card", method);
        },
        handleShowBind(method) {
            if (this.enableBindCardPopup) {
                this.isShowBindPopup = true;
            } else {
                this.$emit("bind-card", method);
            }
        },
        handleHideBindPopup() {
            this.isShowBindPopup = false;
        },
        async handleDeleteCard(item) {
            try {
                await deleteCard({ card_id: item.id });
                this.$emit("refresh-wallet");
            } catch (error) {
                console.error("Failed to delete card:", error);
            }
        },
    },
};
</script>
<style lang="scss">
@use "@/styles/variables.scss" as *;

@keyframes highlight-pulse {
    0% {
        background-color: $background-white;
    }
    50% {
        background-color: rgba($primary-color, 0.2);
    }
    100% {
        background-color: $background-white;
    }
}

@keyframes payment-method-spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

.payment-methods-root {
    width: 100%;
}

.payment-container {
    margin-top: 20px;
    display: flex;
    gap: 9px;
    flex-direction: column;
    align-items: center;

    .payment-header {
        width: $container-width;
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 5px;
    }

    .payment-title {
        display: inline-block;
        width: $container-width;
        text-align: left;
        font-size: 16px;
        font-weight: 600;
        line-height: 19.09px;
        letter-spacing: -0.2px;
    }
}

.payment-container.variant-divider {
    margin-top: 0;
    gap: 0;
    align-items: stretch;
    width: 100%;
    border-top: 1px solid #e6e6e6;

    .payment-title {
        width: 100%;
    }

    .payment-method {
        width: 100%;
        box-shadow: none;
        border-radius: 0;
        background: transparent;
        padding: 14px 16px;
        border-bottom: 1px solid #e6e6e6;
    }
}

.payment-method {
    width: $container-width;
    background: $background-white;
    padding: 15px;
    border-radius: $border-radius;
    border: 1px solid transparent;
    display: flex;
    align-items: center;
    box-shadow: $shadow;
    transition: background-color 0.2s, border-color 0.2s, color 0.2s;

    &.highlight-flash {
        animation: highlight-pulse 1s ease-in-out 1;
    }

    &.active {
        // border-color: var(--theme-selectable-accent-color);
        // background: var(--theme-selectable-accent-bg);

        .balance-center .top,
        .balance-center .current-balance {
            color: var(--theme-selectable-accent-color);
        }

        .right.add-card,
        .right.recharge {
            color: var(--theme-selectable-accent-color);

            &::after {
                border-top-color: var(--theme-selectable-accent-color);
                border-right-color: var(--theme-selectable-accent-color);
            }
        }
    }

    &.touch-active {
        background-color: #e3e3e3 !important;
        transform: scale(1);
    }

    &.pending {
        opacity: 0.72;
    }

    .left {
        width: 28px;
        height: 28px;
        display: flex;
        justify-content: center;
        align-items: center;
        margin-right: 15px;

        img,
        svg {
            height: 100%;
            width: 26px;
            object-fit: contain;
        }

        &.card img {
            max-height: 18px;
        }
    }

    .center {
        flex: 1;
        font-size: 14px;
        font-weight: 500;
        line-height: 15.59px;
        color: #555;
        font-variant-numeric: tabular-nums;
    }

    .right {
        width: 16px;
        height: 16px;
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        letter-spacing: -0.5px;

        &::before {
            content: "";
            color: $background-white;
            background: transparent;
            font-weight: 300;
            font-size: 14px;
            text-align: center;
            width: 100%;
            height: 100%;
            line-height: 17px;
            border-radius: 50%;
            position: absolute;
            border: 1px solid #b0b0b0;
        }

        &.active::before {
            content: "\2713";
            background: var(--theme-check-bg);
            border: none;
        }

        &.bind-card {
            &::before {
                display: none;
            }
        }
        &.add-card {
            width: auto;
            height: 28px;
            border: none;
            padding: 0;
            font-size: 14px;
            font-weight: 500;
            color: var(--theme-selectable-accent-color);
            display: flex;
            align-items: center;

            &::before {
                content: none;
            }

            &::after {
                content: "";
                display: inline-block;
                width: 6px;
                height: 6px;
                border-top: 2px solid var(--theme-selectable-accent-color);
                border-right: 2px solid var(--theme-selectable-accent-color);
                transform: rotate(45deg);
                margin-left: 5px;
            }
        }
    }

    .right.wallet-loading {
        width: 17px;
        height: 17px;
        box-sizing: border-box;
        border-radius: 50%;
        border: 1px solid rgba(49, 131, 255, 0.22);
        border-top-color: var(--theme-selectable-accent-color);
        animation: payment-method-spin 0.9s linear infinite;

        &::before {
            content: none;
        }
    }
}

.payment-method.balance-method {
    .balance-center {
        .top {
            display: flex;
            align-items: center;
            gap: 6px;
            flex-wrap: wrap;

            .current-balance {
                font-size: 14px;
                color: #c2c2c2;
                margin-left: 0;
            }
        }

        .bottom {
            margin-top: 7px;
        }
    }

    .right.recharge {
        font-size: 14px;
        font-weight: 500;
        color: $primary-color;
        background: transparent;
        width: auto;
        height: 28px;
        border: none;
        padding: 0;
        display: flex;
        align-items: center;

        &::before {
            border: none;
            content: none;
        }

        &::after {
            content: "";
            display: inline-block;
            width: 6px;
            height: 6px;
            border-top: 2px solid $primary-color;
            border-right: 2px solid $primary-color;
            transform: rotate(45deg);
            margin-left: 5px;
        }
    }
}
</style>
